import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Clock3, Loader2, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { formatUyMinor } from "@/lib/formatUyMinor";
import {
  getProfessionalBankAccount,
  getProfessionalPayoutEligibility,
  requestProfessionalPayout,
  type ProfessionalPayoutEligibilityLine,
} from "@/services/professionalEarningsService";
import { useTotpStepUp } from "@/components/features/security/use-totp-step-up";

export function ProfessionalPayoutRequestPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string> | null>(null);

  const eligibilityQuery = useQuery({
    queryKey: ["me", "professional", "payouts", "eligibility"],
    queryFn: getProfessionalPayoutEligibility,
  });

  const bankQuery = useQuery({
    queryKey: ["me", "professional", "bank-account"],
    queryFn: getProfessionalBankAccount,
  });

  useEffect(() => {
    if (!bankQuery.isLoading && bankQuery.data === null) {
      toast.error("Configurá tu cuenta bancaria antes de solicitar un pago");
      navigate("/earnings", { replace: true });
    }
  }, [bankQuery.isLoading, bankQuery.data, navigate]);

  const effectiveSelected = useMemo(() => {
    if (selected) return selected;
    // Default: all available lines selected.
    if (eligibilityQuery.data) {
      return new Set(eligibilityQuery.data.availableLines.map((l) => l.id));
    }
    return new Set<string>();
  }, [selected, eligibilityQuery.data]);

  // Requesting a payout is the action this guard exists for: it moves money,
  // and an idle session must prove a fresh code before it can.
  const { guard: stepUpGuard, dialog: stepUpDialog } = useTotpStepUp();

  const mutation = useMutation({
    mutationFn: (lineIds: string[]) =>
      stepUpGuard(() => requestProfessionalPayout({ lineIds })),
    onSuccess: () => {
      toast.success("Solicitud de pago creada");
      void queryClient.invalidateQueries({
        queryKey: ["me", "professional"],
      });
      navigate("/earnings");
    },
    onError: (err: unknown) => {
      const msg =
        err !== null &&
        typeof err === "object" &&
        "response" in err &&
        typeof (err as { response?: { data?: { message?: string } } }).response?.data
          ?.message === "string"
          ? (err as { response: { data: { message: string } } }).response.data.message
          : "No se pudo crear la solicitud de pago";
      toast.error(msg);
    },
  });

  if (eligibilityQuery.isLoading && !eligibilityQuery.data) {
    return (
      <div className="p-4 md:p-8">
        <div className="flex min-h-[40vh] items-center justify-center">
          <LoadingLogo size="lg" />
        </div>
      </div>
    );
  }

  const eligibility = eligibilityQuery.data;
  if (!eligibility) {
    return (
      <div className="p-4 md:p-8">
        <EmptyState
          title="No pudimos cargar tus ingresos"
          message="Volvé a intentarlo en unos segundos."
        />
      </div>
    );
  }

  if (eligibility.pendingRequest) {
    const pending = eligibility.pendingRequest;
    const createdAt = new Date(pending.createdAt);
    const createdAtLabel = `${createdAt.getDate().toString().padStart(2, "0")}/${(
      createdAt.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${createdAt.getFullYear()}`;
    const isProcessing = pending.status === "processing";
    const statusCopy = isProcessing
      ? {
          headerDescription: "Tu pago está en proceso. Te avisamos cuando se acredite.",
          cardTitle: "Tu pago está en proceso",
          cardBody: `Solicitado el ${createdAtLabel}. Estamos transfiriendo los fondos a tu cuenta bancaria. Podrás solicitar otro pago cuando este se acredite.`,
          iconClass: "text-blue-500",
        }
      : {
          headerDescription: "Ya hay una solicitud en revisión. Te avisamos apenas se acredite.",
          cardTitle: "Tu solicitud está en revisión",
          cardBody: `Creada el ${createdAtLabel}. El equipo de Gestionalo la revisa y transfiere a tu cuenta bancaria. Vas a poder solicitar otro pago cuando esta se acredite.`,
          iconClass: "text-amber-500",
        };
    return (
      <div className="p-4 md:p-8">
        <PageHeader
          title="Solicitar pago"
          description={statusCopy.headerDescription}
          onBack={() => navigate("/earnings")}
        />
        <Card className="mt-4">
          <CardContent className="flex flex-col gap-4 p-6">
            <div className="flex items-start gap-3">
              <Clock3 className={`mt-0.5 h-5 w-5 ${statusCopy.iconClass}`} aria-hidden />
              <div>
                <p className="text-base font-semibold">{statusCopy.cardTitle}</p>
                <p className="mt-1 text-sm text-muted-foreground">{statusCopy.cardBody}</p>
              </div>
            </div>
            <div className="rounded-md border bg-muted/40 p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Monto solicitado
              </div>
              <div className="mt-1 text-2xl font-semibold">
                {formatUyMinor(pending.totalMinor, pending.currency)}
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => navigate("/earnings")}>Volver a ingresos</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { availableLines, lockedLines, availableMinor, lockedMinor, currency, holdDays } =
    eligibility;

  const selectedTotalMinor = availableLines
    .filter((l) => effectiveSelected.has(l.id))
    .reduce((s, l) => s + l.amountMinor, 0);

  const toggle = (id: string) => {
    const next = new Set(effectiveSelected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const allSelected =
    availableLines.length > 0 &&
    availableLines.every((l) => effectiveSelected.has(l.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(availableLines.map((l) => l.id)));
  };

  return (
    <>
      <div className="p-4 md:p-8">
        <PageHeader
          title="Solicitar pago"
          description="Elegí qué ingresos querés cobrar. Nosotros realizamos la transferencia y te avisamos cuando esté acreditada."
          onBack={() => navigate("/earnings")}
        />

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Disponible para cobrar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold text-foreground">
                {formatUyMinor(availableMinor, currency ?? "UYU")}
              </p>
              <p className="text-xs text-muted-foreground">
                {availableLines.length} {availableLines.length === 1 ? "ingreso" : "ingresos"} listos
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Lock className="h-4 w-4" aria-hidden />
                Retenido temporalmente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold text-foreground">
                {formatUyMinor(lockedMinor, currency ?? "UYU")}
              </p>
              <p className="text-xs text-muted-foreground">
                Los ingresos se liberan a los {holdDays} días por políticas del proveedor de pagos.
              </p>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-foreground">Seleccioná los ingresos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {availableLines.length === 0 ? (
              <EmptyState
                title="Sin ingresos disponibles todavía"
                message={`Los ingresos están retenidos por ${holdDays} días luego del pago del cliente.`}
              />
            ) : (
              <>
                <div className="flex items-center justify-between border-b pb-2">
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      aria-label="Seleccionar todos"
                    />
                    Seleccionar todos
                  </label>
                  <p className="text-xs text-muted-foreground">
                    {effectiveSelected.size} de {availableLines.length}
                  </p>
                </div>
                <ul className="divide-y">
                  {availableLines.map((line) => (
                    <EligibleLineRow
                      key={line.id}
                      line={line}
                      checked={effectiveSelected.has(line.id)}
                      onToggle={() => toggle(line.id)}
                    />
                  ))}
                </ul>
              </>
            )}

            {lockedLines.length > 0 ? (
              <div className="mt-6 rounded-lg border bg-background/40 p-4">
                <div className="flex items-center gap-2 pb-3 text-sm font-semibold text-foreground">
                  <Clock3 className="h-4 w-4 text-amber-300" aria-hidden />
                  Próximos a liberarse
                </div>
                <ul className="divide-y">
                  {lockedLines.map((line) => (
                    <LockedLineRow key={line.id} line={line} />
                  ))}
                </ul>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <div className="sticky bottom-0 z-10 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-background/90 p-4 backdrop-blur">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Total a cobrar
            </p>
            <p className="text-xl font-semibold text-foreground">
              {formatUyMinor(selectedTotalMinor, currency ?? "UYU")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate("/earnings")}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={
                mutation.isPending ||
                effectiveSelected.size === 0 ||
                availableLines.length === 0
              }
              onClick={() =>
                mutation.mutate(Array.from(effectiveSelected))
              }
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Solicitando…
                </>
              ) : (
                `Solicitar pago de ${formatUyMinor(selectedTotalMinor, currency ?? "UYU")}`
              )}
            </Button>
          </div>
        </div>

        {availableLines.length > 0 && lockedLines.length > 0 ? (
          <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            Solo podés incluir ingresos que ya pasaron el período de retención de {holdDays} días.
          </p>
        ) : null}
      </div>
      {/* MUST be rendered: `guard` awaits a promise this dialog
          settles, so without it a step-up refusal would hang the
          action forever instead of asking for a code. */}
      {stepUpDialog}
    </>
  );
}

function EligibleLineRow({
  line,
  checked,
  onToggle,
}: {
  line: ProfessionalPayoutEligibilityLine;
  checked: boolean;
  onToggle: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const requestId = line.notarialRequestId ?? line.accountantRequestId;
  const title = line.requestLabel ?? "Solicitud";
  return (
    <li className="flex items-center justify-between gap-3 py-3">
      <label
        onClick={(e) => e.stopPropagation()}
        className="shrink-0"
      >
        <Checkbox
          checked={checked}
          onCheckedChange={onToggle}
          aria-label={`Incluir ${title}${line.requesterName ? ` de ${line.requesterName}` : ""}`}
        />
      </label>
      <button
        type="button"
        className="group flex min-w-0 flex-1 items-center justify-between gap-3 rounded px-2 py-1 text-left transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
        onClick={() => {
          if (!requestId) return;
          navigate(`/requests/${requestId}`, {
            state: { from: "/earnings/request-payout" },
          });
        }}
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground group-hover:text-primary">
            {title}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {line.requesterName ?? "—"} ·{" "}
            {new Date(line.createdAt).toLocaleDateString("es-UY")}
          </p>
        </div>
        <span className="shrink-0 text-sm font-medium tabular-nums text-foreground">
          {formatUyMinor(line.amountMinor, line.currency)}
        </span>
      </button>
    </li>
  );
}

function LockedLineRow({
  line,
}: {
  line: ProfessionalPayoutEligibilityLine;
}): JSX.Element {
  const navigate = useNavigate();
  const requestId = line.notarialRequestId ?? line.accountantRequestId;
  const title = line.requestLabel ?? "Solicitud";
  const availableDate = new Date(line.availableAt);
  const daysLeft = Math.max(
    0,
    Math.ceil((availableDate.getTime() - Date.now()) / 86_400_000),
  );
  return (
    <li>
      <button
        type="button"
        disabled={!requestId}
        onClick={() => {
          if (!requestId) return;
          navigate(`/requests/${requestId}`, {
            state: { from: "/earnings/request-payout" },
          });
        }}
        className="group flex w-full items-center justify-between gap-3 py-2.5 text-left text-sm transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none disabled:opacity-80"
      >
        <div className="min-w-0 flex-1 px-2">
          <p className="truncate font-medium text-foreground group-hover:text-primary">
            {title}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {line.requesterName ?? "—"} · disponible el{" "}
            {availableDate.toLocaleDateString("es-UY")} · en {daysLeft}{" "}
            {daysLeft === 1 ? "día" : "días"}
          </p>
        </div>
        <span className="shrink-0 px-2 text-muted-foreground tabular-nums">
          {formatUyMinor(line.amountMinor, line.currency)}
        </span>
      </button>
    </li>
  );
}
